
"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Child } from "@/lib/types";

const currentYear = new Date().getFullYear();

const formSchema = z.object({
  name: z.string().min(1, "Name is required."),
  birthYear: z.coerce.number().int().min(currentYear - 18, "Child must be under 18.").max(currentYear, "Birth year cannot be in the future."),
  birthMonth: z.coerce.number().int().min(1, "Month is required.").max(12),
  gender: z.enum(["boy", "girl", "other"]),
});

type ChildProfileDialogProps = {
  onSave: (data: Omit<Child, 'id' | 'userId'> | Omit<Child, 'userId'>) => void;
  children: React.ReactNode;
  child?: Child | null;
};

export function ChildProfileDialog({ onSave, children, child }: ChildProfileDialogProps) {
  const [open, setOpen] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });
  
  useEffect(() => {
    if (open) {
      const defaultValues = child ? {
          name: child.name,
          birthYear: child.birthYear,
          birthMonth: child.birthMonth,
          gender: child.gender,
      } : {
          name: "",
          birthYear: new Date().getFullYear(),
          birthMonth: new Date().getMonth() + 1,
          gender: "other" as const,
      }
      form.reset(defaultValues);
    }
  }, [child, open, form])

  function onSubmit(values: z.infer<typeof formSchema>) {
    const newChildData = {
      name: values.name,
      birthMonth: values.birthMonth,
      birthYear: values.birthYear,
      gender: values.gender
    };

    if (child) {
      onSave({ ...newChildData, id: child.id });
    } else {
      onSave(newChildData);
    }

    form.reset();
    setOpen(false);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{child ? 'Edit' : 'Add'} Child Profile</DialogTitle>
          <DialogDescription>
            Enter the details for the child.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Alex" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 gap-4">
               <FormField
                control={form.control}
                name="birthMonth"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Birth Month</FormLabel>
                     <Select onValueChange={(val) => field.onChange(parseInt(val))} value={field.value?.toString()}>
                        <FormControl>
                           <SelectTrigger>
                                <SelectValue placeholder="Select month" />
                           </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Array.from({ length: 12 }, (_, i) => (
                            <SelectItem key={i+1} value={(i + 1).toString()}>
                              {new Date(0, i).toLocaleString('default', { month: 'long' })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                     </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
               <FormField
                control={form.control}
                name="birthYear"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Birth Year</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder={currentYear.toString()} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Gender</FormLabel>
                   <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="boy">Boy</SelectItem>
                      <SelectItem value="girl">Girl</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit">Save Profile</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
